import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Zap } from "lucide-react";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";

// ISO (UTC) <-> value for <input type="datetime-local"> (local wall time)
function isoToLocalInput(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const off = d.getTimezoneOffset() * 60000;
    return new Date(d.getTime() - off).toISOString().slice(0, 16);
}
function localInputToIso(val) {
    if (!val) return null;
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d.toISOString();
}

const emptyForm = {
    active: false,
    title: "CyberDay AVX Pro",
    subtitle: "Special prices on selected plugins",
    badge_text: "CYBERDAY",
    cta_text: "View offers",
    cta_link: "/plugins",
    starts_at: "",
    ends_at: "",
};

export default function AdminCampaign() {
    const [form, setForm] = useState(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/campaign");
            setForm({
                active: !!data.active,
                title: data.title || "",
                subtitle: data.subtitle || "",
                badge_text: data.badge_text || "CYBERDAY",
                cta_text: data.cta_text || "View offers",
                cta_link: data.cta_link || "/plugins",
                starts_at: isoToLocalInput(data.starts_at),
                ends_at: isoToLocalInput(data.ends_at),
            });
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const save = async (overrides = {}) => {
        setSaving(true);
        try {
            const merged = { ...form, ...overrides };
            const payload = {
                active: merged.active,
                title: merged.title,
                subtitle: merged.subtitle,
                badge_text: merged.badge_text || "CYBERDAY",
                cta_text: merged.cta_text || "View offers",
                cta_link: merged.cta_link || "/plugins",
                starts_at: localInputToIso(merged.starts_at),
                ends_at: localInputToIso(merged.ends_at),
            };
            await api.put("/admin/campaign", payload);
            toast.success("CyberDay updated");
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (v) => {
        setForm((f) => ({ ...f, active: v }));
        await save({ active: v });
    };

    if (loading) {
        return (
            <DashboardLayout role="admin">
                <div className="text-avx-muted font-mono">Loading…</div>
            </DashboardLayout>
        );
    }

    const endInPast = form.ends_at && new Date(form.ends_at).getTime() < Date.now();

    return (
        <DashboardLayout role="admin">
            <div className="mb-8" data-testid="admin-campaign-page">
                <div className="label-mono mb-3">/ CyberDay</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Campaign & CyberDay</h1>
                <p className="text-avx-muted mt-2 max-w-2xl">
                    Turn on the site-wide banner with a countdown. Prices come from each product's sale price —
                    set those in <span className="text-avx-blue">Products</span>. Discounted plugins show a CYBERDAY badge while the event is live.
                </p>
            </div>

            {/* Master toggle */}
            <div
                className="panel-elevated p-6 mb-6 flex items-center justify-between gap-4"
                style={form.active ? { background: "linear-gradient(90deg, rgba(255,46,154,0.12), rgba(124,58,237,0.12), rgba(34,211,238,0.10))" } : undefined}
            >
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: "linear-gradient(135deg,#FF2E9A,#7C3AED)" }}>
                        <Zap className="w-5 h-5 text-white fill-white" />
                    </div>
                    <div>
                        <div className="font-heading font-bold text-lg">
                            CyberDay is {form.active ? <span className="text-avx-success">LIVE</span> : <span className="text-avx-muted">OFF</span>}
                        </div>
                        <div className="text-xs font-mono text-avx-muted">
                            {form.active ? "The banner is visible across the public site." : "Turn on to show the banner."}
                            {form.active && endInPast && <span className="text-avx-danger"> · End time is in the past — banner is hidden.</span>}
                        </div>
                    </div>
                </div>
                <Switch checked={form.active} onCheckedChange={toggleActive} data-testid="campaign-active-toggle" />
            </div>

            {/* Settings */}
            <div className="panel p-6 space-y-5 max-w-2xl">
                <div>
                    <Label className="label-mono">Banner title</Label>
                    <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-title" />
                </div>
                <div>
                    <Label className="label-mono">Subtitle</Label>
                    <Textarea rows={2} className="mt-2 bg-avx-bg border-avx-line" value={form.subtitle} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="campaign-subtitle" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="label-mono">Card badge text</Label>
                        <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono uppercase" value={form.badge_text} onChange={(e) => setForm({ ...form, badge_text: e.target.value.toUpperCase() })} data-testid="campaign-badge" />
                    </div>
                    <div>
                        <Label className="label-mono">CTA text</Label>
                        <Input className="mt-2 bg-avx-bg border-avx-line h-11" value={form.cta_text} onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="campaign-cta-text" />
                    </div>
                </div>
                <div>
                    <Label className="label-mono">CTA link</Label>
                    <Input className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.cta_link} onChange={(e) => setForm({ ...form, cta_link: e.target.value })} placeholder="/plugins" data-testid="campaign-cta-link" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <Label className="label-mono">Starts at</Label>
                        <Input type="datetime-local" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="campaign-starts" />
                    </div>
                    <div>
                        <Label className="label-mono">Ends at (countdown target)</Label>
                        <Input type="datetime-local" className="mt-2 bg-avx-bg border-avx-line h-11 font-mono" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="campaign-ends" />
                    </div>
                </div>
                <p className="text-xs text-avx-muted font-mono">
                    The banner auto-hides once the end time passes (even if still ON). Leave "Ends at" empty for no countdown.
                </p>
                <div className="flex justify-end pt-2 border-t border-avx-line">
                    <button onClick={() => save()} disabled={saving} className="btn-primary px-6 py-2.5 text-xs font-mono uppercase tracking-[0.2em] rounded-sm disabled:opacity-60" data-testid="campaign-save-btn">
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </DashboardLayout>
    );
}
